import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { CommonButton } from '@/components/ui/common-button';
import { CommonSelect } from '@/components/ui/common-select';
import { CommonTextarea } from '@/components/ui/common-textarea';
import Loader from '@/components/ui/loader';
import { useToast } from '@/components/ui/use-toast';
import masterDataService from '@/services/masterDataService';
import tripService from '@/services/tripService';

const ChangeDriverDialog = ({ open, onOpenChange, tripData, onChangeDriver }) => {
  const [drivers, setDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [driversLoading, setDriversLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const searchTimeoutRef = useRef(null);
  
  const { toast } = useToast();

  useEffect(() => {
    if (open) {
      setFormLoading(true);
      fetchDrivers().then(() => {
        setFormLoading(false);
      });
      resetForm();
    }
  }, [open]);

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      fetchDrivers(searchTerm);
    }, 300);
    
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm]);

  const fetchDrivers = async (search = '') => {
    setDriversLoading(true);
    try {
      const response = await masterDataService.getDrivers(0, 1000, false, false, search);
      setDrivers(response.data || response || []);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load drivers. Please try again.",
        variant: "destructive",
      });
    } finally {
      setDriversLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedDriver('');
    setReason('');
    setErrors({});
    setSearchTerm('');
  };

  const validateForm = () => {
    const newErrors = {};
    if (!selectedDriver) {
      newErrors.driver_id = 'Driver is required';
    }
    if (!reason.trim()) {
      newErrors.reason = 'Reason is required';
    } else if (reason.trim().length < 10) {
      newErrors.reason = 'Reason must be at least 10 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      const driverChangeData = {
        driver_id: selectedDriver,
        change_reason: reason.trim()
      };
      
      const response = await tripService.changeDriver(tripData.id, driverChangeData);
      
      toast({
        title: "Success",
        description: "Driver has been successfully changed for this trip.",
      });
      
      onChangeDriver(response);
      
      onOpenChange(false);
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to change driver. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const driverOptions = drivers.map(driver => ({
    value: driver.id,
    label: `${driver.user?.username || driver.id} (${driver.license_number || 'No License'})`
  }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Change Driver</DialogTitle>
        </DialogHeader>
        
        {formLoading ? (
          <div className="flex justify-center items-center py-8">
            <Loader size="md" />
          </div>
        ) : (
          <div className="space-y-4 py-4">
            <CommonSelect
              label="Select New Driver"
              id="driver"
              value={selectedDriver}
              onValueChange={setSelectedDriver}
              options={driverOptions}
              error={errors.driver_id}
              required
              placeholder="Select a driver"
              loading={driversLoading}
              searchable
              onSearchChange={setSearchTerm}
              searchValue={searchTerm}
            />
            
            <CommonTextarea
              label="Reason for Change"
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Enter reason for changing driver..."
              error={errors.reason}
              required
            />
            <p className="text-xs text-muted-foreground -mt-2">Minimum 10 characters</p>
          </div>
        )}
        
        {!formLoading && (
          <div className="flex justify-end space-x-2">
            <CommonButton
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </CommonButton>
            <CommonButton
              type="button"
              onClick={handleSubmit}
              disabled={loading || driversLoading}
              loading={loading}
            >
              Change Driver
            </CommonButton>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ChangeDriverDialog;